import { useEffect, useId, useRef, useState } from 'preact/hooks'
import { Loader2, Send } from 'lucide-preact'
import { sendingDomainsApi } from '@/api/sendingDomains'
import { TextBox } from '@/components/common/TextBox'
import { useUserProfile } from '@/hooks/useUserProfile'
import type { SendingDomainViewDto } from '@/types/sendingDomains'
import { getDisplayErrorMessages, parseValidationErrors } from '@/utils/errorHandling'
import { developerHelper, developerTodo, instructionsSent, instructionsSentRuns } from '@/utils/sendingDomainStatus'
import { CopyRuns } from './DomainPageParts'
import { GHOST_BUTTON, SMALL_SECONDARY_BUTTON, TEXT_LINK } from './styles'

const EMAIL_FIELD = 'developer-email'
const NOTE_FIELD = 'developer-note'

interface DeveloperInstructionsProps {
  clientCompanyId: string
  domain: SendingDomainViewDto
  companyDomain: string | null
  onSent: (domain: SendingDomainViewDto) => void
}

type FocusTarget = 'email' | 'note' | 'opener' | 'sent'

/**
 * "Send to your developer": emails whoever manages the company's DNS what is still failing on a setting-up domain.
 * Shown only while something is left for a developer to do; a refusal stays under the field it is about.
 */
export function DeveloperInstructions({ clientCompanyId, domain, companyDomain, onSent }: DeveloperInstructionsProps) {
  const titleId = useId()
  const { userProfile } = useUserProfile()
  const [open, setOpen] = useState(false)
  const [email, setEmail] = useState('')
  const [note, setNote] = useState('')
  const [sending, setSending] = useState(false)
  const [emailErrors, setEmailErrors] = useState<string[]>([])
  const [noteErrors, setNoteErrors] = useState<string[]>([])
  // The sent line is a status only on the render right after a send, so it is announced once, not on every visit.
  const [justSent, setJustSent] = useState(false)
  const openerRef = useRef<HTMLButtonElement>(null)
  const sentRef = useRef<HTMLParagraphElement>(null)
  const focusAfterRender = useRef<FocusTarget | null>(null)

  // Focus moves once the element it moves to exists: the form opens, closes back to its button, or collapses to
  // the sent line. The sent line can arrive a render after the send, once the page has the updated domain, so a
  // target that is not drawn yet waits for the next render.
  useEffect(() => {
    const target = focusAfterRender.current
    if (!target) return
    const element = target === 'email' ? document.getElementById(EMAIL_FIELD)
      : target === 'note' ? document.getElementById(NOTE_FIELD)
        : target === 'opener' ? openerRef.current
          : sentRef.current
    if (!element) return
    focusAfterRender.current = null
    element.focus()
  })

  const todo = developerTodo(domain, companyDomain)
  if (todo.pendingRecords === 0 && !todo.needsRedirect) return null
  const sent = instructionsSent(domain, userProfile?.Id ?? null)

  const openForm = () => {
    // Send again usually goes to the same person, so their address is already filled in.
    if (sent) setEmail(sent.to)
    setOpen(true)
    setJustSent(false)
    focusAfterRender.current = 'email'
  }

  const cancel = () => {
    setOpen(false)
    setEmailErrors([])
    setNoteErrors([])
    focusAfterRender.current = 'opener'
  }

  const submit = async (event: Event) => {
    event.preventDefault()
    if (sending || !email.trim()) return
    setSending(true)
    setEmailErrors([])
    setNoteErrors([])
    const res = await sendingDomainsApi.sendDeveloperInstructions(clientCompanyId, domain.Id, {
      Email: email.trim(),
      Note: note.trim() || null,
    })
    setSending(false)

    if (res.error || !res.data) {
      // The API names the field a refusal is about; anything else (the daily cap, a failed send) is about the
      // address, so it goes under that.
      const refusals = parseValidationErrors({ error: res.error })
      const forNote = refusals.filter(refusal => refusal.FieldName === 'Note').map(refusal => refusal.Message)
      const forEmail = refusals.filter(refusal => refusal.FieldName !== 'Note').map(refusal => refusal.Message)
      setNoteErrors(forNote)
      setEmailErrors(forEmail.length > 0 || forNote.length > 0
        ? forEmail
        : getDisplayErrorMessages(res.error, 'The instructions could not be sent.'))
      focusAfterRender.current = forEmail.length === 0 && forNote.length > 0 ? 'note' : 'email'
      return
    }

    onSent(res.data)
    setOpen(false)
    setEmail('')
    setNote('')
    setJustSent(true)
    focusAfterRender.current = 'sent'
  }

  if (!open) {
    if (!sent) {
      return (
        <div>
          <button ref={openerRef} type="button" onClick={openForm} className={SMALL_SECONDARY_BUTTON}>
            <Send className="h-4 w-4" aria-hidden="true" /> Send to your developer
          </button>
        </div>
      )
    }
    return (
      <div className="grid grid-cols-[20px_minmax(0,1fr)] gap-x-3">
        <Send className="mt-0.5 h-4 w-4 text-slate-500" aria-hidden="true" />
        <p
          ref={sentRef}
          tabIndex={-1}
          role={justSent ? 'status' : undefined}
          className="text-sm text-slate-600 focus:outline-none dark:text-slate-400"
        >
          <CopyRuns runs={instructionsSentRuns(sent)} />{' '}
          <button ref={openerRef} type="button" onClick={openForm} className={TEXT_LINK}>Send again</button>
        </p>
      </div>
    )
  }

  return (
    <div className="grid grid-cols-[20px_minmax(0,1fr)] gap-x-3">
      <Send className="mt-0.5 h-5 w-5 text-slate-500" aria-hidden="true" />
      <form onSubmit={submit} aria-labelledby={titleId} noValidate className="space-y-4">
        <h4 id={titleId} className="text-sm font-semibold text-slate-900 dark:text-white">Send to your developer</h4>
        <TextBox
          name={EMAIL_FIELD}
          type="email"
          label="Developer's email"
          value={email}
          onChange={setEmail}
          autoComplete="off"
          placeholder={companyDomain ? `it@${companyDomain}` : undefined}
          error={emailErrors.join(' ') || undefined}
          disabled={sending}
          containerClassName="max-w-sm"
        />
        <TextBox
          name={NOTE_FIELD}
          multiline
          rows={3}
          label="Note (optional)"
          value={note}
          onChange={setNote}
          error={noteErrors.join(' ') || undefined}
          disabled={sending}
          containerClassName="max-w-xl"
        />
        <div className="space-y-3">
          <p className="max-w-prose text-xs text-slate-600 dark:text-slate-400">{developerHelper(todo, companyDomain)}</p>
          <div className="flex items-center gap-2">
            <button type="submit" disabled={sending || !email.trim()} className={SMALL_SECONDARY_BUTTON}>
              {sending
                ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                : <Send className="h-4 w-4" aria-hidden="true" />}
              Send instructions
            </button>
            <button type="button" onClick={cancel} disabled={sending} className={GHOST_BUTTON}>Cancel</button>
          </div>
        </div>
      </form>
    </div>
  )
}
